/**
 * FikoMaterialDB - Malzeme Veritabani
 *
 * Tum uretim surecleri icin malzeme ozellikleri, fiyatlari ve
 * uretilebilirlik parametrelerini yonetir.
 *
 * - Malzeme fiyatlari (TL/kg, USD/kg, EUR/kg)
 * - Yogunluk, islenebilirlik, dokum ozellikleri
 * - Surece-ozel uyumluluk matrisi
 * - Cekme payi, min duvar kalinligi, cekis acisi kurallari
 */

// ============================================================
// PARA BIRIMI
// ============================================================

export type Currency = 'TRY' | 'USD' | 'EUR';

export interface CurrencyRates {
    TRY_USD: number;
    TRY_EUR: number;
    USD_EUR: number;
    updatedAt: Date;
}

// ============================================================
// MALZEME KATEGORILERI
// ============================================================

export type MaterialCategory =
    | 'ferrous_cast_iron'      // Dokme demir
    | 'ferrous_steel'          // Celik
    | 'ferrous_stainless'      // Paslanmaz celik
    | 'non_ferrous_aluminum'   // Aluminyum
    | 'non_ferrous_copper'     // Bakir/Bronz/Pirinc
    | 'non_ferrous_zinc'       // Cinko
    | 'non_ferrous_magnesium'  // Magnezyum
    | 'non_ferrous_titanium'   // Titanyum
    | 'non_ferrous_nickel'     // Nikel alasimlari
    | 'polymer_thermoplastic'  // Termoplastik
    | 'polymer_thermoset'      // Termoset
    | 'polymer_elastomer'      // Elastomer
    | 'composite'              // Kompozit
    | 'ceramic';               // Seramik

export type ManufacturingProcessType =
    | 'sand_casting'
    | 'die_casting'
    | 'investment_casting'
    | 'shell_mold_casting'
    | 'centrifugal_casting'
    | 'cnc_milling_3axis'
    | 'cnc_milling_5axis'
    | 'cnc_turning'
    | 'cnc_edm'
    | 'cnc_grinding'
    | 'sheet_metal_laser'
    | 'sheet_metal_plasma'
    | 'sheet_metal_waterjet'
    | 'sheet_metal_punching'
    | 'sheet_metal_bending'
    | 'injection_molding'
    | 'blow_molding'
    | 'fdm_3d_print'
    | 'sla_3d_print'
    | 'sls_3d_print'
    | 'dmls_3d_print'
    | 'mjf_3d_print'
    | 'open_die_forging'
    | 'closed_die_forging'
    | 'welding_mig'
    | 'welding_tig'
    | 'welding_spot'
    | 'welding_laser'
    | 'heat_treatment'
    | 'surface_anodizing'
    | 'surface_plating'
    | 'surface_painting'
    | 'surface_powder_coating';

// ============================================================
// MALZEME TANIMLARI
// ============================================================

export interface MaterialProperties {
    id: string;
    name: string;
    nameTR: string;
    category: MaterialCategory;

    // Fiziksel ozellikler
    density: number;               // kg/cm3
    meltingPoint: number;          // Celsius
    thermalConductivity: number;   // W/(m*K)
    specificHeat: number;          // J/(kg*K)

    // Mekanik ozellikler
    tensileStrength: number;       // MPa
    yieldStrength: number;         // MPa
    elongation: number;            // %
    hardness: number;              // HB (Brinell)
    elasticModulus: number;        // GPa

    // Islenebilirlik (0-100, 100=en kolay)
    machinabilityIndex: number;
    weldabilityIndex: number;      // 0-100
    castabilityIndex: number;      // 0-100
    formabilityIndex: number;      // 0-100 (sac metal)

    // Fiyat bilgileri
    pricePerKg: number;            // Varsayilan para biriminde
    priceCurrency: Currency;
    castingPricePerKg?: number;    // Dokum sonrasi fiyat/kg
    barStockPricePerKg?: number;   // Cubuk stok fiyat/kg
    sheetPricePerKg?: number;      // Sac fiyat/kg
    wirePricePerKg?: number;       // Tel fiyat/kg (kaynak)
    powderPricePerKg?: number;     // Toz fiyat/kg (3D baski)

    // Dokum ozellikleri
    shrinkageAllowance: number;    // % (cekme payi)
    pouringTemperature?: number;   // Celsius (dokum sicakligi)
    fluidityIndex?: number;        // 0-100 (akiskanlik)

    // Surec uyumlulugu
    compatibleProcesses: ManufacturingProcessType[];

    // Surece-ozel min duvar kalinligi (mm)
    minWallThickness: Record<string, number>;

    // Surece-ozel min cekis acisi (derece)
    minDraftAngle: Record<string, number>;

    // ── Sprint 1.4: Genisletilmis malzeme ozellikleri ──

    /** Darbe dayanimi - Charpy (J/m) */
    impactStrength?: number;
    /** Yorulma dayanimi - 10^7 cevrim (MPa) */
    fatigueStrength?: number;
    /** Kirilma toklugu (MPa*sqrt(m)) */
    fractureToughness?: number;
    /** Kimyasal direnç - anahtar kimyasal maddelere karsi */
    chemicalResistance?: Record<string, 'excellent' | 'good' | 'fair' | 'poor'>;
    /** UV stabilitesi */
    uvStability?: 'excellent' | 'good' | 'fair' | 'poor';
    /** Nem emme orani (% 24 saat) */
    moistureAbsorption?: number;
    /** Yanmazlik sinifi (UL94 V-0, V-1, V-2, HB) */
    flammabilityRating?: string;
    /** Geri donusturulebilir mi */
    recyclable?: boolean;
    /** RoHS uyumlu mu */
    roHSCompliant?: boolean;
    /** Malzeme tipi siniflandirmasi */
    materialType?: 'metal' | 'polymer' | 'composite' | 'ceramic';
}

// ============================================================
// ISLEM PARAMETRELERI
// ============================================================

export interface MachineParameters {
    hourlyRate: number;            // Saat ucreti (varsayilan para birimi)
    currency: Currency;
    setupTimeMinutes: number;      // Hazirlik suresi
    toolChangeSeconds: number;     // Takim degisim suresi
    efficiencyFactor: number;      // 0-1 verimlilik carpani
}

export interface ToleranceCostMultiplier {
    toleranceRange: string;        // +-0.5mm, +-0.1mm, etc.
    toleranceMM: number;           // Numerik deger
    costMultiplier: number;        // Carpan
    requiredProcess?: string;      // Gereken islem (taslama, honlama, vb.)
}

export interface SurfaceFinishCostMultiplier {
    raValue: number;               // Ra (um)
    description: string;
    descriptionTR: string;
    costMultiplier: number;
    requiredProcess: string;
}

// ============================================================
// MALZEME VERITABANI
// ============================================================

export class FikoMaterialDB {
    private materials: Map<string, MaterialProperties> = new Map();
    private machineParams: Map<ManufacturingProcessType, MachineParameters> = new Map();
    private toleranceCosts: ToleranceCostMultiplier[] = [];
    private surfaceFinishCosts: SurfaceFinishCostMultiplier[] = [];
    private currencyRates: CurrencyRates;

    constructor() {
        this.currencyRates = {
            TRY_USD: 34.0,
            TRY_EUR: 37.0,
            USD_EUR: 1.09,
            updatedAt: new Date()
        };
        this.initMaterials();
        this.initMachineParameters();
        this.initToleranceCosts();
        this.initSurfaceFinishCosts();
    }

    // ============================================================
    // PUBLIC API
    // ============================================================

    getMaterial(id: string): MaterialProperties | undefined {
        return this.materials.get(id);
    }

    getAllMaterials(): MaterialProperties[] {
        return Array.from(this.materials.values());
    }

    getMaterialsByCategory(category: MaterialCategory): MaterialProperties[] {
        return this.getAllMaterials().filter(m => m.category === category);
    }

    getMaterialsForProcess(process: ManufacturingProcessType): MaterialProperties[] {
        return this.getAllMaterials().filter(m =>
            m.compatibleProcesses.includes(process)
        );
    }

    getMachineParams(process: ManufacturingProcessType): MachineParameters | undefined {
        return this.machineParams.get(process);
    }

    getToleranceCostMultiplier(toleranceMM: number): number {
        for (const t of this.toleranceCosts) {
            if (toleranceMM >= t.toleranceMM) {
                return t.costMultiplier;
            }
        }
        return this.toleranceCosts[this.toleranceCosts.length - 1]?.costMultiplier || 10.0;
    }

    getSurfaceFinishCostMultiplier(raValue: number): number {
        let closest = this.surfaceFinishCosts[0];
        for (const sf of this.surfaceFinishCosts) {
            if (raValue <= sf.raValue) {
                closest = sf;
            }
        }
        return closest?.costMultiplier || 1.0;
    }

    /** Fiyati farkli para birimine cevir */
    convertPrice(amount: number, from: Currency, to: Currency): number {
        if (from === to) return amount;

        if (from === 'USD' && to === 'TRY') return amount * this.currencyRates.TRY_USD;
        if (from === 'TRY' && to === 'USD') return amount / this.currencyRates.TRY_USD;
        if (from === 'EUR' && to === 'TRY') return amount * this.currencyRates.TRY_EUR;
        if (from === 'TRY' && to === 'EUR') return amount / this.currencyRates.TRY_EUR;
        if (from === 'USD' && to === 'EUR') return amount / this.currencyRates.USD_EUR;
        if (from === 'EUR' && to === 'USD') return amount * this.currencyRates.USD_EUR;

        return amount;
    }

    /** Doviz kurlarini guncelle */
    updateCurrencyRates(rates: Partial<CurrencyRates>): void {
        Object.assign(this.currencyRates, rates, { updatedAt: new Date() });
    }

    /** Malzeme fiyatini guncelle */
    updateMaterialPrice(materialId: string, pricePerKg: number, currency?: Currency): void {
        const mat = this.materials.get(materialId);
        if (mat) {
            mat.pricePerKg = pricePerKg;
            if (currency) mat.priceCurrency = currency;
        }
    }

    /** Makine saat ucretini guncelle */
    updateMachineRate(process: ManufacturingProcessType, hourlyRate: number): void {
        const params = this.machineParams.get(process);
        if (params) {
            params.hourlyRate = hourlyRate;
        }
    }

    // ============================================================
    // MALZEME VERITABANI INIT
    // ============================================================

    private initMaterials(): void {
        // ---- DOKME DEMIR ----
        this.addMaterial({
            id: 'GJL-250',
            name: 'Gray Cast Iron GJL-250',
            nameTR: 'Gri Dokme Demir GJL-250',
            category: 'ferrous_cast_iron',
            density: 0.0072, meltingPoint: 1200, thermalConductivity: 46, specificHeat: 490,
            tensileStrength: 250, yieldStrength: 165, elongation: 0.5, hardness: 210, elasticModulus: 100,
            machinabilityIndex: 80, weldabilityIndex: 30, castabilityIndex: 90, formabilityIndex: 5,
            pricePerKg: 0.65, priceCurrency: 'USD', castingPricePerKg: 2.0, barStockPricePerKg: 1.2,
            shrinkageAllowance: 1.0, pouringTemperature: 1350, fluidityIndex: 85,
            compatibleProcesses: ['sand_casting', 'shell_mold_casting', 'centrifugal_casting', 'cnc_milling_3axis', 'cnc_milling_5axis', 'cnc_turning', 'cnc_grinding'],
            minWallThickness: { sand_casting: 4.0, shell_mold_casting: 3.5, centrifugal_casting: 5.0, cnc_milling_3axis: 1.0 },
            minDraftAngle: { sand_casting: 2.0, shell_mold_casting: 1.5 }
        });

        this.addMaterial({
            id: 'GJS-400',
            name: 'Ductile Iron GJS-400-15',
            nameTR: 'Kuresel Grafitli Dokme Demir GJS-400',
            category: 'ferrous_cast_iron',
            density: 0.0071, meltingPoint: 1150, thermalConductivity: 36, specificHeat: 500,
            tensileStrength: 400, yieldStrength: 250, elongation: 15, hardness: 160, elasticModulus: 170,
            machinabilityIndex: 70, weldabilityIndex: 40, castabilityIndex: 80, formabilityIndex: 10,
            pricePerKg: 0.80, priceCurrency: 'USD', castingPricePerKg: 2.5, barStockPricePerKg: 1.5,
            shrinkageAllowance: 0.8, pouringTemperature: 1380, fluidityIndex: 75,
            compatibleProcesses: ['sand_casting', 'shell_mold_casting', 'centrifugal_casting', 'cnc_milling_3axis', 'cnc_milling_5axis', 'cnc_turning', 'cnc_grinding'],
            minWallThickness: { sand_casting: 4.0, shell_mold_casting: 3.0 },
            minDraftAngle: { sand_casting: 2.0, shell_mold_casting: 1.5 }
        });

        this.addMaterial({
            id: 'GJS-600',
            name: 'Ductile Iron GJS-600-3',
            nameTR: 'Kuresel Grafitli Dokme Demir GJS-600',
            category: 'ferrous_cast_iron',
            density: 0.0071, meltingPoint: 1150, thermalConductivity: 32, specificHeat: 500,
            tensileStrength: 600, yieldStrength: 370, elongation: 3, hardness: 230, elasticModulus: 170,
            machinabilityIndex: 60, weldabilityIndex: 35, castabilityIndex: 75, formabilityIndex: 5,
            pricePerKg: 0.90, priceCurrency: 'USD', castingPricePerKg: 2.8, barStockPricePerKg: 1.6,
            shrinkageAllowance: 0.85, pouringTemperature: 1400, fluidityIndex: 70,
            compatibleProcesses: ['sand_casting', 'shell_mold_casting', 'centrifugal_casting', 'cnc_milling_3axis', 'cnc_milling_5axis', 'cnc_turning', 'cnc_grinding'],
            minWallThickness: { sand_casting: 4.5, shell_mold_casting: 3.5 },
            minDraftAngle: { sand_casting: 2.0 }
        });

        // ---- CELIK ----
        this.addMaterial({
            id: 'ST37',
            name: 'Carbon Steel S235JR (St37)',
            nameTR: 'Karbonlu Celik S235JR (St37)',
            category: 'ferrous_steel',
            density: 0.00785, meltingPoint: 1510, thermalConductivity: 50, specificHeat: 490,
            tensileStrength: 370, yieldStrength: 235, elongation: 26, hardness: 120, elasticModulus: 210,
            machinabilityIndex: 65, weldabilityIndex: 90, castabilityIndex: 60, formabilityIndex: 80,
            pricePerKg: 0.70, priceCurrency: 'USD', castingPricePerKg: 1.8, barStockPricePerKg: 1.0, sheetPricePerKg: 0.95, wirePricePerKg: 1.5,
            shrinkageAllowance: 2.2, pouringTemperature: 1580, fluidityIndex: 50,
            compatibleProcesses: ['sand_casting', 'investment_casting', 'cnc_milling_3axis', 'cnc_milling_5axis', 'cnc_turning', 'cnc_edm', 'cnc_grinding', 'sheet_metal_laser', 'sheet_metal_plasma', 'sheet_metal_waterjet', 'sheet_metal_punching', 'sheet_metal_bending', 'open_die_forging', 'closed_die_forging', 'welding_mig', 'welding_tig', 'welding_spot'],
            minWallThickness: { sand_casting: 5.0, investment_casting: 2.0, cnc_milling_3axis: 0.8, sheet_metal_laser: 0.5 },
            minDraftAngle: { sand_casting: 2.5, investment_casting: 0.5, closed_die_forging: 5.0 }
        });

        this.addMaterial({
            id: 'CK45',
            name: 'Carbon Steel C45 (CK45)',
            nameTR: 'Karbonlu Celik C45 (CK45)',
            category: 'ferrous_steel',
            density: 0.00785, meltingPoint: 1510, thermalConductivity: 49, specificHeat: 486,
            tensileStrength: 620, yieldStrength: 340, elongation: 14, hardness: 207, elasticModulus: 210,
            machinabilityIndex: 55, weldabilityIndex: 60, castabilityIndex: 55, formabilityIndex: 50,
            pricePerKg: 0.85, priceCurrency: 'USD', barStockPricePerKg: 1.2,
            shrinkageAllowance: 2.3, pouringTemperature: 1580,
            compatibleProcesses: ['sand_casting', 'investment_casting', 'cnc_milling_3axis', 'cnc_milling_5axis', 'cnc_turning', 'cnc_edm', 'cnc_grinding', 'open_die_forging', 'closed_die_forging', 'welding_mig', 'welding_tig'],
            minWallThickness: { sand_casting: 5.5, cnc_milling_3axis: 0.8 },
            minDraftAngle: { sand_casting: 2.5, closed_die_forging: 5.0 }
        });

        this.addMaterial({
            id: '4140',
            name: 'Alloy Steel 4140 (42CrMo4)',
            nameTR: 'Alaşımlı Çelik 4140 (42CrMo4)',
            category: 'ferrous_steel',
            density: 0.00785, meltingPoint: 1500, thermalConductivity: 42, specificHeat: 473,
            tensileStrength: 1020, yieldStrength: 655, elongation: 12, hardness: 302, elasticModulus: 210,
            machinabilityIndex: 50, weldabilityIndex: 45, castabilityIndex: 50, formabilityIndex: 30,
            pricePerKg: 1.10, priceCurrency: 'USD', barStockPricePerKg: 1.5,
            shrinkageAllowance: 2.3,
            compatibleProcesses: ['sand_casting', 'investment_casting', 'cnc_milling_3axis', 'cnc_milling_5axis', 'cnc_turning', 'cnc_edm', 'cnc_grinding', 'open_die_forging', 'closed_die_forging'],
            minWallThickness: { sand_casting: 6.0, cnc_milling_3axis: 1.0 },
            minDraftAngle: { sand_casting: 3.0, closed_die_forging: 5.0 }
        });

        // ---- PASLANMAZ CELIK ----
        this.addMaterial({
            id: 'AISI304',
            name: 'Stainless Steel 304 (1.4301)',
            nameTR: 'Paslanmaz Celik 304 (1.4301)',
            category: 'ferrous_stainless',
            density: 0.00793, meltingPoint: 1450, thermalConductivity: 16, specificHeat: 500,
            tensileStrength: 620, yieldStrength: 210, elongation: 45, hardness: 187, elasticModulus: 200,
            machinabilityIndex: 35, weldabilityIndex: 85, castabilityIndex: 55, formabilityIndex: 70,
            pricePerKg: 2.50, priceCurrency: 'USD', castingPricePerKg: 4.0, barStockPricePerKg: 3.5, sheetPricePerKg: 3.2, wirePricePerKg: 4.5,
            shrinkageAllowance: 2.8, pouringTemperature: 1560, fluidityIndex: 45,
            compatibleProcesses: ['investment_casting', 'sand_casting', 'cnc_milling_3axis', 'cnc_milling_5axis', 'cnc_turning', 'cnc_edm', 'cnc_grinding', 'sheet_metal_laser', 'sheet_metal_waterjet', 'sheet_metal_bending', 'welding_tig', 'welding_laser'],
            minWallThickness: { sand_casting: 5.5, investment_casting: 1.5, cnc_milling_3axis: 0.8, sheet_metal_laser: 0.5 },
            minDraftAngle: { sand_casting: 3.0, investment_casting: 0.5 }
        });

        this.addMaterial({
            id: 'AISI316',
            name: 'Stainless Steel 316L (1.4404)',
            nameTR: 'Paslanmaz Celik 316L (1.4404)',
            category: 'ferrous_stainless',
            density: 0.008, meltingPoint: 1400, thermalConductivity: 14, specificHeat: 500,
            tensileStrength: 580, yieldStrength: 220, elongation: 50, hardness: 170, elasticModulus: 200,
            machinabilityIndex: 30, weldabilityIndex: 80, castabilityIndex: 50, formabilityIndex: 65,
            pricePerKg: 3.00, priceCurrency: 'USD', castingPricePerKg: 5.0, barStockPricePerKg: 4.0, sheetPricePerKg: 3.8,
            shrinkageAllowance: 2.8,
            compatibleProcesses: ['investment_casting', 'sand_casting', 'cnc_milling_3axis', 'cnc_milling_5axis', 'cnc_turning', 'cnc_edm', 'cnc_grinding', 'sheet_metal_laser', 'sheet_metal_waterjet', 'sheet_metal_bending', 'welding_tig', 'welding_laser', 'dmls_3d_print'],
            minWallThickness: { investment_casting: 1.5, cnc_milling_3axis: 0.8, dmls_3d_print: 1.0 },
            minDraftAngle: { sand_casting: 3.0, investment_casting: 0.5 }
        });

        // ---- ALUMINYUM ----
        this.addMaterial({
            id: 'AL6061',
            name: 'Aluminum 6061-T6',
            nameTR: 'Aluminyum 6061-T6',
            category: 'non_ferrous_aluminum',
            density: 0.0027, meltingPoint: 660, thermalConductivity: 167, specificHeat: 896,
            tensileStrength: 310, yieldStrength: 276, elongation: 12, hardness: 95, elasticModulus: 69,
            machinabilityIndex: 90, weldabilityIndex: 60, castabilityIndex: 50, formabilityIndex: 70,
            pricePerKg: 2.80, priceCurrency: 'USD', barStockPricePerKg: 3.5, sheetPricePerKg: 3.2,
            shrinkageAllowance: 1.5,
            compatibleProcesses: ['cnc_milling_3axis', 'cnc_milling_5axis', 'cnc_turning', 'cnc_grinding', 'sheet_metal_laser', 'sheet_metal_waterjet', 'sheet_metal_bending', 'welding_tig', 'welding_mig', 'surface_anodizing'],
            minWallThickness: { cnc_milling_3axis: 0.6, sheet_metal_laser: 0.5 },
            minDraftAngle: {}
        });

        this.addMaterial({
            id: 'AL7075',
            name: 'Aluminum 7075-T6',
            nameTR: 'Aluminyum 7075-T6',
            category: 'non_ferrous_aluminum',
            density: 0.0028, meltingPoint: 635, thermalConductivity: 130, specificHeat: 860,
            tensileStrength: 572, yieldStrength: 503, elongation: 11, hardness: 150, elasticModulus: 72,
            machinabilityIndex: 85, weldabilityIndex: 20, castabilityIndex: 30, formabilityIndex: 40,
            pricePerKg: 4.50, priceCurrency: 'USD', barStockPricePerKg: 5.5,
            shrinkageAllowance: 1.5,
            compatibleProcesses: ['cnc_milling_3axis', 'cnc_milling_5axis', 'cnc_turning', 'cnc_grinding', 'surface_anodizing'],
            minWallThickness: { cnc_milling_3axis: 0.6 },
            minDraftAngle: {}
        });

        this.addMaterial({
            id: 'A356',
            name: 'Aluminum A356-T6 (Casting)',
            nameTR: 'Aluminyum A356-T6 (Dokum)',
            category: 'non_ferrous_aluminum',
            density: 0.00267, meltingPoint: 615, thermalConductivity: 151, specificHeat: 900,
            tensileStrength: 262, yieldStrength: 186, elongation: 5, hardness: 90, elasticModulus: 72,
            machinabilityIndex: 85, weldabilityIndex: 50, castabilityIndex: 95, formabilityIndex: 20,
            pricePerKg: 2.70, priceCurrency: 'USD', castingPricePerKg: 5.0,
            shrinkageAllowance: 1.8, pouringTemperature: 720, fluidityIndex: 90,
            compatibleProcesses: ['sand_casting', 'die_casting', 'investment_casting', 'shell_mold_casting', 'cnc_milling_3axis', 'cnc_milling_5axis', 'cnc_turning', 'surface_anodizing'],
            minWallThickness: { sand_casting: 3.5, die_casting: 1.0, investment_casting: 1.2 },
            minDraftAngle: { sand_casting: 1.5, die_casting: 1.0, investment_casting: 0.5 }
        });

        this.addMaterial({
            id: 'ADC12',
            name: 'Aluminum ADC12 (Die Cast)',
            nameTR: 'Aluminyum ADC12 (Basincli Dokum)',
            category: 'non_ferrous_aluminum',
            density: 0.00271, meltingPoint: 580, thermalConductivity: 92, specificHeat: 963,
            tensileStrength: 310, yieldStrength: 155, elongation: 3, hardness: 85, elasticModulus: 71,
            machinabilityIndex: 80, weldabilityIndex: 30, castabilityIndex: 95, formabilityIndex: 10,
            pricePerKg: 2.60, priceCurrency: 'USD', castingPricePerKg: 3.5,
            shrinkageAllowance: 0.6, pouringTemperature: 680, fluidityIndex: 95,
            compatibleProcesses: ['die_casting', 'cnc_milling_3axis', 'cnc_turning'],
            minWallThickness: { die_casting: 0.8 },
            minDraftAngle: { die_casting: 1.0 }
        });

        // ---- BRONZ / BAKIR ----
        this.addMaterial({
            id: 'CuSn12',
            name: 'Phosphor Bronze CuSn12',
            nameTR: 'Fosfor Bronz CuSn12',
            category: 'non_ferrous_copper',
            density: 0.00875, meltingPoint: 880, thermalConductivity: 50, specificHeat: 380,
            tensileStrength: 350, yieldStrength: 200, elongation: 8, hardness: 120, elasticModulus: 110,
            machinabilityIndex: 60, weldabilityIndex: 40, castabilityIndex: 85, formabilityIndex: 20,
            pricePerKg: 12.0, priceCurrency: 'USD', castingPricePerKg: 18.0,
            shrinkageAllowance: 1.3, pouringTemperature: 1100, fluidityIndex: 80,
            compatibleProcesses: ['sand_casting', 'centrifugal_casting', 'investment_casting', 'cnc_milling_3axis', 'cnc_turning'],
            minWallThickness: { sand_casting: 3.5, centrifugal_casting: 4.0 },
            minDraftAngle: { sand_casting: 2.0 }
        });

        this.addMaterial({
            id: 'CuAl10',
            name: 'Aluminum Bronze CuAl10Fe5Ni5',
            nameTR: 'Aluminyum Bronz CuAl10Fe5Ni5',
            category: 'non_ferrous_copper',
            density: 0.0076, meltingPoint: 1040, thermalConductivity: 44, specificHeat: 420,
            tensileStrength: 650, yieldStrength: 280, elongation: 13, hardness: 170, elasticModulus: 120,
            machinabilityIndex: 45, weldabilityIndex: 50, castabilityIndex: 75, formabilityIndex: 15,
            pricePerKg: 10.0, priceCurrency: 'USD', castingPricePerKg: 15.0,
            shrinkageAllowance: 2.1, pouringTemperature: 1200, fluidityIndex: 65,
            compatibleProcesses: ['sand_casting', 'centrifugal_casting', 'investment_casting', 'cnc_milling_3axis', 'cnc_turning'],
            minWallThickness: { sand_casting: 4.0, centrifugal_casting: 5.0 },
            minDraftAngle: { sand_casting: 2.5 }
        });

        // ---- CINKO (Basincli Dokum) ----
        this.addMaterial({
            id: 'ZAMAK3',
            name: 'Zinc Alloy Zamak 3',
            nameTR: 'Cinko Alasimi Zamak 3',
            category: 'non_ferrous_zinc',
            density: 0.0066, meltingPoint: 386, thermalConductivity: 113, specificHeat: 418,
            tensileStrength: 283, yieldStrength: 228, elongation: 10, hardness: 82, elasticModulus: 86,
            machinabilityIndex: 85, weldabilityIndex: 15, castabilityIndex: 98, formabilityIndex: 10,
            pricePerKg: 3.50, priceCurrency: 'USD', castingPricePerKg: 4.0,
            shrinkageAllowance: 0.7, pouringTemperature: 420, fluidityIndex: 98,
            compatibleProcesses: ['die_casting', 'cnc_milling_3axis', 'cnc_turning', 'surface_plating'],
            minWallThickness: { die_casting: 0.5 },
            minDraftAngle: { die_casting: 0.5 }
        });

        // ---- TITANYUM ----
        this.addMaterial({
            id: 'Ti6Al4V',
            name: 'Titanium Ti-6Al-4V (Grade 5)',
            nameTR: 'Titanyum Ti-6Al-4V (Grade 5)',
            category: 'non_ferrous_titanium',
            density: 0.00443, meltingPoint: 1668, thermalConductivity: 7, specificHeat: 560,
            tensileStrength: 950, yieldStrength: 880, elongation: 14, hardness: 334, elasticModulus: 114,
            machinabilityIndex: 20, weldabilityIndex: 60, castabilityIndex: 40, formabilityIndex: 25,
            pricePerKg: 25.0, priceCurrency: 'USD', barStockPricePerKg: 35.0, powderPricePerKg: 80.0,
            shrinkageAllowance: 2.0,
            compatibleProcesses: ['investment_casting', 'cnc_milling_5axis', 'cnc_turning', 'cnc_edm', 'cnc_grinding', 'dmls_3d_print', 'welding_tig', 'closed_die_forging'],
            minWallThickness: { investment_casting: 1.5, cnc_milling_5axis: 0.8, dmls_3d_print: 1.0 },
            minDraftAngle: { investment_casting: 0.5, closed_die_forging: 7.0 }
        });

        // ---- MAGNEZYUM ----
        this.addMaterial({
            id: 'AZ91D',
            name: 'Magnesium AZ91D',
            nameTR: 'Magnezyum AZ91D',
            category: 'non_ferrous_magnesium',
            density: 0.00181, meltingPoint: 595, thermalConductivity: 72, specificHeat: 1020,
            tensileStrength: 230, yieldStrength: 150, elongation: 3, hardness: 63, elasticModulus: 45,
            machinabilityIndex: 95, weldabilityIndex: 25, castabilityIndex: 90, formabilityIndex: 15,
            pricePerKg: 5.50, priceCurrency: 'USD', castingPricePerKg: 8.0,
            shrinkageAllowance: 0.6, pouringTemperature: 680, fluidityIndex: 88,
            compatibleProcesses: ['die_casting', 'cnc_milling_3axis', 'cnc_turning'],
            minWallThickness: { die_casting: 1.0 },
            minDraftAngle: { die_casting: 1.5 }
        });

        // ---- PLASTIKLER ----
        this.addMaterial({
            id: 'ABS',
            name: 'ABS (Acrylonitrile Butadiene Styrene)',
            nameTR: 'ABS',
            category: 'polymer_thermoplastic',
            density: 0.00105, meltingPoint: 230, thermalConductivity: 0.17, specificHeat: 1400,
            tensileStrength: 44, yieldStrength: 42, elongation: 25, hardness: 108, elasticModulus: 2.3,
            machinabilityIndex: 75, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 85,
            pricePerKg: 2.20, priceCurrency: 'USD', powderPricePerKg: 25.0,
            shrinkageAllowance: 0.5,
            compatibleProcesses: ['injection_molding', 'fdm_3d_print', 'cnc_milling_3axis'],
            minWallThickness: { injection_molding: 1.2, fdm_3d_print: 0.8 },
            minDraftAngle: { injection_molding: 1.0 }
        });

        this.addMaterial({
            id: 'PA6',
            name: 'Nylon 6 (PA6)',
            nameTR: 'Naylon 6 (PA6)',
            category: 'polymer_thermoplastic',
            density: 0.00114, meltingPoint: 220, thermalConductivity: 0.25, specificHeat: 1700,
            tensileStrength: 80, yieldStrength: 70, elongation: 60, hardness: 120, elasticModulus: 2.8,
            machinabilityIndex: 70, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 80,
            pricePerKg: 3.50, priceCurrency: 'USD', powderPricePerKg: 50.0,
            shrinkageAllowance: 1.5,
            compatibleProcesses: ['injection_molding', 'sls_3d_print', 'mjf_3d_print', 'cnc_milling_3axis'],
            minWallThickness: { injection_molding: 0.8, sls_3d_print: 0.8, mjf_3d_print: 0.5 },
            minDraftAngle: { injection_molding: 0.5 }
        });

        this.addMaterial({
            id: 'PC',
            name: 'Polycarbonate (PC)',
            nameTR: 'Polikarbonat (PC)',
            category: 'polymer_thermoplastic',
            density: 0.0012, meltingPoint: 267, thermalConductivity: 0.20, specificHeat: 1200,
            tensileStrength: 65, yieldStrength: 62, elongation: 110, hardness: 125, elasticModulus: 2.4,
            machinabilityIndex: 75, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 80,
            pricePerKg: 3.80, priceCurrency: 'USD',
            shrinkageAllowance: 0.6,
            compatibleProcesses: ['injection_molding', 'cnc_milling_3axis'],
            minWallThickness: { injection_molding: 1.0 },
            minDraftAngle: { injection_molding: 1.0 }
        });

        this.addMaterial({
            id: 'POM',
            name: 'Acetal (POM)',
            nameTR: 'Asetal (POM)',
            category: 'polymer_thermoplastic',
            density: 0.00142, meltingPoint: 175, thermalConductivity: 0.31, specificHeat: 1460,
            tensileStrength: 68, yieldStrength: 65, elongation: 40, hardness: 120, elasticModulus: 3.0,
            machinabilityIndex: 90, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 60,
            pricePerKg: 2.80, priceCurrency: 'USD',
            shrinkageAllowance: 2.0,
            compatibleProcesses: ['injection_molding', 'cnc_milling_3axis', 'cnc_turning'],
            minWallThickness: { injection_molding: 0.8 },
            minDraftAngle: { injection_molding: 0.5 }
        });

        this.addMaterial({
            id: 'PP',
            name: 'Polypropylene (PP)',
            nameTR: 'Polipropilen (PP)',
            category: 'polymer_thermoplastic',
            density: 0.0009, meltingPoint: 165, thermalConductivity: 0.12, specificHeat: 1800,
            tensileStrength: 35, yieldStrength: 30, elongation: 400, hardness: 72, elasticModulus: 1.5,
            machinabilityIndex: 70, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 90,
            pricePerKg: 1.30, priceCurrency: 'USD',
            shrinkageAllowance: 1.5,
            compatibleProcesses: ['injection_molding', 'blow_molding'],
            minWallThickness: { injection_molding: 0.8, blow_molding: 0.6 },
            minDraftAngle: { injection_molding: 1.0 }
        });

        // ---- 3D BASKI RECINELERI ----
        this.addMaterial({
            id: 'PLA',
            name: 'PLA (Polylactic Acid)',
            nameTR: 'PLA',
            category: 'polymer_thermoplastic',
            density: 0.00124, meltingPoint: 180, thermalConductivity: 0.13, specificHeat: 1800,
            tensileStrength: 50, yieldStrength: 45, elongation: 6, hardness: 80, elasticModulus: 3.5,
            machinabilityIndex: 60, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 10,
            pricePerKg: 20.0, priceCurrency: 'USD', powderPricePerKg: 20.0,
            shrinkageAllowance: 0.3,
            compatibleProcesses: ['fdm_3d_print'],
            minWallThickness: { fdm_3d_print: 1.0 },
            minDraftAngle: {}
        });

        this.addMaterial({
            id: 'RESIN_STD',
            name: 'Standard Photopolymer Resin',
            nameTR: 'Standart Fotopolimer Recine',
            category: 'polymer_thermoset',
            density: 0.0012, meltingPoint: 0, thermalConductivity: 0.18, specificHeat: 1100,
            tensileStrength: 50, yieldStrength: 40, elongation: 6, hardness: 85, elasticModulus: 2.8,
            machinabilityIndex: 40, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 0,
            pricePerKg: 40.0, priceCurrency: 'USD',
            shrinkageAllowance: 0.2,
            compatibleProcesses: ['sla_3d_print'],
            minWallThickness: { sla_3d_print: 0.4 },
            minDraftAngle: {}
        });

        // ---- ENGINEERING POLYMERS ----
        this.addMaterial({
            id: 'PEEK',
            name: 'Polyether Ether Ketone (PEEK)',
            nameTR: 'PEEK',
            category: 'polymer_thermoplastic',
            density: 0.00132, meltingPoint: 343, thermalConductivity: 0.25, specificHeat: 1340,
            tensileStrength: 100, yieldStrength: 91, elongation: 30, hardness: 85, elasticModulus: 4.0,
            machinabilityIndex: 50, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 40,
            pricePerKg: 70.0, priceCurrency: 'USD',
            shrinkageAllowance: 1.2,
            compatibleProcesses: ['injection_molding', 'cnc_milling_3axis', 'cnc_milling_5axis', 'cnc_turning', 'fdm_3d_print', 'sls_3d_print'],
            minWallThickness: { injection_molding: 0.6, cnc_milling_3axis: 0.5, fdm_3d_print: 0.8, sls_3d_print: 0.7 },
            minDraftAngle: { injection_molding: 0.5 },
            impactStrength: 80,
            fatigueStrength: 40,
            fractureToughness: 5.0,
            chemicalResistance: { acids: 'excellent', solvents: 'excellent', alkalis: 'good' },
            uvStability: 'good',
            flammabilityRating: 'V-0',
            recyclable: true,
            roHSCompliant: true,
            materialType: 'polymer'
        });

        this.addMaterial({
            id: 'PEI',
            name: 'Polyetherimide (PEI/Ultem)',
            nameTR: 'PEI Ultem',
            category: 'polymer_thermoplastic',
            density: 0.00127, meltingPoint: 340, thermalConductivity: 0.22, specificHeat: 1270,
            tensileStrength: 85, yieldStrength: 72, elongation: 60, hardness: 78, elasticModulus: 3.0,
            machinabilityIndex: 55, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 50,
            pricePerKg: 35.0, priceCurrency: 'USD',
            shrinkageAllowance: 0.7,
            compatibleProcesses: ['injection_molding', 'cnc_milling_3axis', 'fdm_3d_print'],
            minWallThickness: { injection_molding: 0.8, cnc_milling_3axis: 0.6, fdm_3d_print: 1.0 },
            minDraftAngle: { injection_molding: 0.5 },
            impactStrength: 50,
            chemicalResistance: { acids: 'good', solvents: 'good' },
            flammabilityRating: 'V-0',
            recyclable: true,
            roHSCompliant: true,
            materialType: 'polymer'
        });

        this.addMaterial({
            id: 'PPS',
            name: 'Polyphenylene Sulfide (PPS)',
            nameTR: 'PPS',
            category: 'polymer_thermoplastic',
            density: 0.00135, meltingPoint: 280, thermalConductivity: 0.29, specificHeat: 1090,
            tensileStrength: 75, yieldStrength: 65, elongation: 3, hardness: 95, elasticModulus: 3.8,
            machinabilityIndex: 45, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 30,
            pricePerKg: 12.0, priceCurrency: 'USD',
            shrinkageAllowance: 0.8,
            compatibleProcesses: ['injection_molding', 'cnc_milling_3axis'],
            minWallThickness: { injection_molding: 0.8, cnc_milling_3axis: 0.7 },
            minDraftAngle: { injection_molding: 1.0 },
            impactStrength: 15,
            chemicalResistance: { acids: 'excellent', solvents: 'excellent', alkalis: 'excellent' },
            flammabilityRating: 'V-0',
            recyclable: true,
            roHSCompliant: true,
            materialType: 'polymer'
        });

        this.addMaterial({
            id: 'PA66_GF30',
            name: 'Nylon 66 Glass Filled 30%',
            nameTR: 'Cam Elyafli Naylon 66 (GF30)',
            category: 'polymer_thermoplastic',
            density: 0.00140, meltingPoint: 260, thermalConductivity: 0.37, specificHeat: 1260,
            tensileStrength: 185, yieldStrength: 125, elongation: 4, hardness: 90, elasticModulus: 9.5,
            machinabilityIndex: 50, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 30,
            pricePerKg: 4.50, priceCurrency: 'USD',
            shrinkageAllowance: 0.6,
            compatibleProcesses: ['injection_molding', 'cnc_milling_3axis'],
            minWallThickness: { injection_molding: 0.8, cnc_milling_3axis: 0.8 },
            minDraftAngle: { injection_molding: 1.0 },
            impactStrength: 100,
            moistureAbsorption: 1.2,
            flammabilityRating: 'V-2',
            recyclable: true,
            roHSCompliant: true,
            materialType: 'polymer'
        });

        this.addMaterial({
            id: 'PA_CF',
            name: 'Carbon Fiber Reinforced Nylon',
            nameTR: 'Karbon Elyafli Naylon',
            category: 'polymer_thermoplastic',
            density: 0.00125, meltingPoint: 260, thermalConductivity: 0.50, specificHeat: 1300,
            tensileStrength: 210, yieldStrength: 170, elongation: 3, hardness: 88, elasticModulus: 20.0,
            machinabilityIndex: 40, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 20,
            pricePerKg: 30.0, priceCurrency: 'USD',
            shrinkageAllowance: 0.5,
            compatibleProcesses: ['injection_molding', 'fdm_3d_print', 'sls_3d_print'],
            minWallThickness: { injection_molding: 0.8, fdm_3d_print: 1.0, sls_3d_print: 0.8 },
            minDraftAngle: { injection_molding: 1.0 },
            impactStrength: 60,
            fatigueStrength: 80,
            flammabilityRating: 'V-1',
            recyclable: false,
            roHSCompliant: true,
            materialType: 'polymer'
        });

        this.addMaterial({
            id: 'PMMA',
            name: 'Polymethyl Methacrylate (Acrylic)',
            nameTR: 'Akrilik (PMMA)',
            category: 'polymer_thermoplastic',
            density: 0.00119, meltingPoint: 160, thermalConductivity: 0.19, specificHeat: 1460,
            tensileStrength: 70, yieldStrength: 60, elongation: 5, hardness: 92, elasticModulus: 3.1,
            machinabilityIndex: 70, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 60,
            pricePerKg: 3.50, priceCurrency: 'USD',
            shrinkageAllowance: 0.5,
            compatibleProcesses: ['injection_molding', 'cnc_milling_3axis', 'sla_3d_print'],
            minWallThickness: { injection_molding: 1.0, cnc_milling_3axis: 0.8, sla_3d_print: 0.5 },
            minDraftAngle: { injection_molding: 1.0 },
            impactStrength: 15,
            uvStability: 'excellent',
            flammabilityRating: 'HB',
            recyclable: true,
            roHSCompliant: true,
            materialType: 'polymer'
        });

        this.addMaterial({
            id: 'PC_ABS',
            name: 'PC/ABS Blend',
            nameTR: 'PC/ABS Karisim',
            category: 'polymer_thermoplastic',
            density: 0.00113, meltingPoint: 230, thermalConductivity: 0.20, specificHeat: 1500,
            tensileStrength: 55, yieldStrength: 45, elongation: 60, hardness: 70, elasticModulus: 2.3,
            machinabilityIndex: 75, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 75,
            pricePerKg: 4.00, priceCurrency: 'USD',
            shrinkageAllowance: 0.6,
            compatibleProcesses: ['injection_molding', 'fdm_3d_print'],
            minWallThickness: { injection_molding: 1.0, fdm_3d_print: 1.0 },
            minDraftAngle: { injection_molding: 1.0 },
            impactStrength: 400,
            chemicalResistance: { acids: 'good', solvents: 'fair' },
            flammabilityRating: 'V-0',
            recyclable: true,
            roHSCompliant: true,
            materialType: 'polymer'
        });

        this.addMaterial({
            id: 'LCP',
            name: 'Liquid Crystal Polymer (LCP)',
            nameTR: 'Sivi Kristal Polimer (LCP)',
            category: 'polymer_thermoplastic',
            density: 0.00141, meltingPoint: 280, thermalConductivity: 0.20, specificHeat: 1100,
            tensileStrength: 170, yieldStrength: 150, elongation: 2, hardness: 85, elasticModulus: 10.0,
            machinabilityIndex: 35, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 30,
            pricePerKg: 25.0, priceCurrency: 'USD',
            shrinkageAllowance: 0.3,
            compatibleProcesses: ['injection_molding'],
            minWallThickness: { injection_molding: 0.5 },
            minDraftAngle: { injection_molding: 0.5 },
            impactStrength: 25,
            chemicalResistance: { acids: 'excellent', solvents: 'excellent' },
            flammabilityRating: 'V-0',
            recyclable: true,
            roHSCompliant: true,
            materialType: 'polymer'
        });

        // ---- HIGH PERFORMANCE METALS ----
        this.addMaterial({
            id: 'INCONEL625',
            name: 'Inconel 625',
            nameTR: 'Inconel 625',
            category: 'non_ferrous_nickel',
            density: 0.00844, meltingPoint: 1350, thermalConductivity: 9.8, specificHeat: 410,
            tensileStrength: 827, yieldStrength: 414, elongation: 30, hardness: 185, elasticModulus: 205,
            machinabilityIndex: 12, weldabilityIndex: 70, castabilityIndex: 60, formabilityIndex: 25,
            pricePerKg: 35.0, priceCurrency: 'USD', barStockPricePerKg: 60.0,
            shrinkageAllowance: 2.5, pouringTemperature: 1400, fluidityIndex: 55,
            compatibleProcesses: ['cnc_milling_3axis', 'cnc_milling_5axis', 'cnc_turning', 'cnc_edm', 'cnc_grinding', 'investment_casting', 'dmls_3d_print', 'welding_tig'],
            minWallThickness: { cnc_milling_3axis: 1.0, cnc_milling_5axis: 1.0, investment_casting: 2.0, dmls_3d_print: 1.0 },
            minDraftAngle: { investment_casting: 1.0 },
            impactStrength: 150,
            fatigueStrength: 300,
            fractureToughness: 170,
            chemicalResistance: { acids: 'excellent', solvents: 'excellent', alkalis: 'excellent' },
            recyclable: true,
            roHSCompliant: true,
            materialType: 'metal'
        });

        this.addMaterial({
            id: 'INCONEL718',
            name: 'Inconel 718',
            nameTR: 'Inconel 718',
            category: 'non_ferrous_nickel',
            density: 0.00819, meltingPoint: 1336, thermalConductivity: 11.4, specificHeat: 435,
            tensileStrength: 1241, yieldStrength: 1034, elongation: 12, hardness: 363, elasticModulus: 205,
            machinabilityIndex: 10, weldabilityIndex: 65, castabilityIndex: 55, formabilityIndex: 20,
            pricePerKg: 40.0, priceCurrency: 'USD', barStockPricePerKg: 70.0,
            shrinkageAllowance: 2.6, pouringTemperature: 1400, fluidityIndex: 50,
            compatibleProcesses: ['cnc_milling_5axis', 'cnc_turning', 'cnc_edm', 'cnc_grinding', 'investment_casting', 'dmls_3d_print', 'welding_tig'],
            minWallThickness: { cnc_milling_5axis: 1.0, investment_casting: 2.0, dmls_3d_print: 1.0 },
            minDraftAngle: { investment_casting: 1.0 },
            impactStrength: 120,
            fatigueStrength: 550,
            fractureToughness: 100,
            chemicalResistance: { acids: 'excellent', alkalis: 'excellent' },
            recyclable: true,
            roHSCompliant: true,
            materialType: 'metal'
        });

        this.addMaterial({
            id: 'HASTELLOY_C276',
            name: 'Hastelloy C-276',
            nameTR: 'Hastelloy C-276',
            category: 'non_ferrous_nickel',
            density: 0.00890, meltingPoint: 1370, thermalConductivity: 10.2, specificHeat: 427,
            tensileStrength: 785, yieldStrength: 355, elongation: 40, hardness: 190, elasticModulus: 205,
            machinabilityIndex: 8, weldabilityIndex: 75, castabilityIndex: 50, formabilityIndex: 30,
            pricePerKg: 50.0, priceCurrency: 'USD',
            shrinkageAllowance: 2.7,
            compatibleProcesses: ['cnc_milling_3axis', 'cnc_milling_5axis', 'cnc_turning', 'cnc_edm', 'welding_tig'],
            minWallThickness: { cnc_milling_3axis: 1.0, cnc_milling_5axis: 1.0 },
            minDraftAngle: {},
            impactStrength: 200,
            fatigueStrength: 250,
            chemicalResistance: { acids: 'excellent', solvents: 'excellent', alkalis: 'excellent' },
            recyclable: true,
            roHSCompliant: true,
            materialType: 'metal'
        });

        this.addMaterial({
            id: 'MONEL400',
            name: 'Monel 400',
            nameTR: 'Monel 400',
            category: 'non_ferrous_nickel',
            density: 0.00880, meltingPoint: 1350, thermalConductivity: 21.8, specificHeat: 427,
            tensileStrength: 550, yieldStrength: 240, elongation: 40, hardness: 125, elasticModulus: 179,
            machinabilityIndex: 25, weldabilityIndex: 70, castabilityIndex: 60, formabilityIndex: 40,
            pricePerKg: 25.0, priceCurrency: 'USD',
            shrinkageAllowance: 2.4,
            compatibleProcesses: ['cnc_milling_3axis', 'cnc_turning', 'cnc_edm', 'welding_tig', 'welding_mig'],
            minWallThickness: { cnc_milling_3axis: 1.0 },
            minDraftAngle: {},
            chemicalResistance: { acids: 'excellent', alkalis: 'excellent' },
            recyclable: true,
            roHSCompliant: true,
            materialType: 'metal'
        });

        this.addMaterial({
            id: 'WASPALOY',
            name: 'Waspaloy',
            nameTR: 'Waspaloy',
            category: 'non_ferrous_nickel',
            density: 0.00836, meltingPoint: 1330, thermalConductivity: 11.6, specificHeat: 502,
            tensileStrength: 1275, yieldStrength: 795, elongation: 25, hardness: 330, elasticModulus: 213,
            machinabilityIndex: 8, weldabilityIndex: 50, castabilityIndex: 60, formabilityIndex: 15,
            pricePerKg: 55.0, priceCurrency: 'USD',
            shrinkageAllowance: 2.8, pouringTemperature: 1420, fluidityIndex: 48,
            compatibleProcesses: ['cnc_milling_5axis', 'cnc_turning', 'cnc_edm', 'investment_casting'],
            minWallThickness: { cnc_milling_5axis: 1.2, investment_casting: 2.5 },
            minDraftAngle: { investment_casting: 1.5 },
            impactStrength: 100,
            fatigueStrength: 500,
            recyclable: true,
            roHSCompliant: true,
            materialType: 'metal'
        });

        // ---- LIGHT ALLOYS ----
        this.addMaterial({
            id: 'TI6AL4V_ELI',
            name: 'Titanium Ti-6Al-4V ELI (Grade 23)',
            nameTR: 'Titanyum Ti-6Al-4V ELI (Grade 23)',
            category: 'non_ferrous_titanium',
            density: 0.00443, meltingPoint: 1660, thermalConductivity: 6.7, specificHeat: 526,
            tensileStrength: 860, yieldStrength: 795, elongation: 10, hardness: 349, elasticModulus: 114,
            machinabilityIndex: 15, weldabilityIndex: 70, castabilityIndex: 45, formabilityIndex: 20,
            pricePerKg: 80.0, priceCurrency: 'USD',
            shrinkageAllowance: 2.1, pouringTemperature: 1750, fluidityIndex: 35,
            compatibleProcesses: ['cnc_milling_5axis', 'cnc_turning', 'cnc_edm', 'investment_casting', 'dmls_3d_print', 'welding_tig'],
            minWallThickness: { cnc_milling_5axis: 0.8, investment_casting: 1.8, dmls_3d_print: 1.0 },
            minDraftAngle: { investment_casting: 1.0 },
            impactStrength: 80,
            fatigueStrength: 500,
            fractureToughness: 100,
            chemicalResistance: { acids: 'excellent', alkalis: 'excellent' },
            recyclable: true,
            roHSCompliant: true,
            materialType: 'metal'
        });

        this.addMaterial({
            id: 'AL_LI_2195',
            name: 'Aluminum-Lithium 2195',
            nameTR: 'Aluminyum-Lityum 2195',
            category: 'non_ferrous_aluminum',
            density: 0.00271, meltingPoint: 630, thermalConductivity: 100, specificHeat: 900,
            tensileStrength: 540, yieldStrength: 500, elongation: 6, hardness: 155, elasticModulus: 78,
            machinabilityIndex: 60, weldabilityIndex: 55, castabilityIndex: 40, formabilityIndex: 35,
            pricePerKg: 25.0, priceCurrency: 'USD',
            shrinkageAllowance: 1.6,
            compatibleProcesses: ['cnc_milling_3axis', 'cnc_milling_5axis', 'cnc_turning'],
            minWallThickness: { cnc_milling_3axis: 0.8, cnc_milling_5axis: 0.8 },
            minDraftAngle: {},
            impactStrength: 50,
            fatigueStrength: 200,
            recyclable: true,
            roHSCompliant: true,
            materialType: 'metal'
        });

        this.addMaterial({
            id: 'MG_AZ91D',
            name: 'Magnesium AZ91D',
            nameTR: 'Magnezyum AZ91D',
            category: 'non_ferrous_magnesium',
            density: 0.00181, meltingPoint: 595, thermalConductivity: 72, specificHeat: 1020,
            tensileStrength: 230, yieldStrength: 160, elongation: 3, hardness: 63, elasticModulus: 45,
            machinabilityIndex: 90, weldabilityIndex: 25, castabilityIndex: 85, formabilityIndex: 30,
            pricePerKg: 6.0, priceCurrency: 'USD', castingPricePerKg: 8.0,
            shrinkageAllowance: 0.6, pouringTemperature: 680, fluidityIndex: 88,
            compatibleProcesses: ['die_casting', 'cnc_milling_3axis', 'cnc_turning', 'injection_molding'],
            minWallThickness: { die_casting: 1.0, cnc_milling_3axis: 0.8, injection_molding: 1.0 },
            minDraftAngle: { die_casting: 1.5, injection_molding: 1.0 },
            impactStrength: 10,
            flammabilityRating: 'Combustible',
            recyclable: true,
            roHSCompliant: true,
            materialType: 'metal'
        });

        this.addMaterial({
            id: 'MG_ZE41',
            name: 'Magnesium ZE41',
            nameTR: 'Magnezyum ZE41',
            category: 'non_ferrous_magnesium',
            density: 0.00183, meltingPoint: 610, thermalConductivity: 63, specificHeat: 960,
            tensileStrength: 205, yieldStrength: 135, elongation: 4, hardness: 60, elasticModulus: 45,
            machinabilityIndex: 85, weldabilityIndex: 20, castabilityIndex: 80, formabilityIndex: 25,
            pricePerKg: 12.0, priceCurrency: 'USD',
            shrinkageAllowance: 0.7, pouringTemperature: 700, fluidityIndex: 80,
            compatibleProcesses: ['sand_casting', 'investment_casting', 'cnc_milling_3axis', 'cnc_turning'],
            minWallThickness: { sand_casting: 4.0, investment_casting: 2.0, cnc_milling_3axis: 0.8 },
            minDraftAngle: { sand_casting: 2.0, investment_casting: 1.0 },
            recyclable: true,
            roHSCompliant: true,
            materialType: 'metal'
        });

        // ---- COMPOSITES ----
        this.addMaterial({
            id: 'CFRP_UD',
            name: 'Carbon Fiber/Epoxy Unidirectional',
            nameTR: 'Karbon Fiber/Epoksi Tek Yonlu',
            category: 'composite',
            density: 0.00160, meltingPoint: 0, thermalConductivity: 5.0, specificHeat: 1100,
            tensileStrength: 1500, yieldStrength: 1200, elongation: 1.5, hardness: 0, elasticModulus: 135,
            machinabilityIndex: 30, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 10,
            pricePerKg: 50.0, priceCurrency: 'USD',
            shrinkageAllowance: 0.1,
            compatibleProcesses: ['cnc_milling_3axis', 'cnc_milling_5axis', 'sheet_metal_waterjet'],
            minWallThickness: { cnc_milling_3axis: 1.0, cnc_milling_5axis: 1.0, sheet_metal_waterjet: 0.5 },
            minDraftAngle: {},
            impactStrength: 30,
            fatigueStrength: 600,
            fractureToughness: 30,
            uvStability: 'good',
            flammabilityRating: 'V-1',
            recyclable: false,
            roHSCompliant: true,
            materialType: 'composite'
        });

        this.addMaterial({
            id: 'CFRP_WOVEN',
            name: 'Carbon Fiber/Epoxy Woven',
            nameTR: 'Karbon Fiber/Epoksi Dokuma',
            category: 'composite',
            density: 0.00155, meltingPoint: 0, thermalConductivity: 3.5, specificHeat: 1100,
            tensileStrength: 600, yieldStrength: 500, elongation: 1.2, hardness: 0, elasticModulus: 70,
            machinabilityIndex: 35, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 15,
            pricePerKg: 40.0, priceCurrency: 'USD',
            shrinkageAllowance: 0.1,
            compatibleProcesses: ['cnc_milling_3axis', 'cnc_milling_5axis', 'sheet_metal_waterjet'],
            minWallThickness: { cnc_milling_3axis: 1.0, cnc_milling_5axis: 1.0, sheet_metal_waterjet: 0.5 },
            minDraftAngle: {},
            impactStrength: 50,
            fractureToughness: 40,
            recyclable: false,
            roHSCompliant: true,
            materialType: 'composite'
        });

        this.addMaterial({
            id: 'GFRP',
            name: 'Glass Fiber/Polyester',
            nameTR: 'Cam Elyaf/Polyester',
            category: 'composite',
            density: 0.00185, meltingPoint: 0, thermalConductivity: 0.30, specificHeat: 900,
            tensileStrength: 250, yieldStrength: 200, elongation: 2, hardness: 0, elasticModulus: 20,
            machinabilityIndex: 50, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 40,
            pricePerKg: 8.0, priceCurrency: 'USD',
            shrinkageAllowance: 0.2,
            compatibleProcesses: ['cnc_milling_3axis', 'sheet_metal_waterjet'],
            minWallThickness: { cnc_milling_3axis: 1.5, sheet_metal_waterjet: 1.0 },
            minDraftAngle: {},
            impactStrength: 80,
            uvStability: 'fair',
            recyclable: false,
            roHSCompliant: true,
            materialType: 'composite'
        });

        this.addMaterial({
            id: 'KEVLAR_EPOXY',
            name: 'Kevlar/Epoxy',
            nameTR: 'Kevlar/Epoksi',
            category: 'composite',
            density: 0.00145, meltingPoint: 0, thermalConductivity: 0.04, specificHeat: 1400,
            tensileStrength: 1100, yieldStrength: 900, elongation: 2.5, hardness: 0, elasticModulus: 80,
            machinabilityIndex: 25, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 20,
            pricePerKg: 60.0, priceCurrency: 'USD',
            shrinkageAllowance: 0.1,
            compatibleProcesses: ['cnc_milling_3axis', 'sheet_metal_waterjet'],
            minWallThickness: { cnc_milling_3axis: 1.2, sheet_metal_waterjet: 0.8 },
            minDraftAngle: {},
            impactStrength: 200,
            fractureToughness: 55,
            recyclable: false,
            roHSCompliant: true,
            materialType: 'composite'
        });

        // ---- SPECIAL MATERIALS ----
        this.addMaterial({
            id: 'WC_CO',
            name: 'Tungsten Carbide (WC-Co)',
            nameTR: 'Tungsten Karbur (WC-Co)',
            category: 'ceramic',
            density: 0.01500, meltingPoint: 2870, thermalConductivity: 110, specificHeat: 200,
            tensileStrength: 350, yieldStrength: 0, elongation: 0, hardness: 1600, elasticModulus: 620,
            machinabilityIndex: 5, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 0,
            pricePerKg: 80.0, priceCurrency: 'USD', powderPricePerKg: 120.0,
            shrinkageAllowance: 0.5,
            compatibleProcesses: ['cnc_edm', 'cnc_grinding'],
            minWallThickness: { cnc_edm: 0.5, cnc_grinding: 0.5 },
            minDraftAngle: {},
            impactStrength: 5,
            fractureToughness: 12,
            recyclable: true,
            roHSCompliant: true,
            materialType: 'ceramic'
        });

        this.addMaterial({
            id: 'CuBe',
            name: 'Copper-Beryllium C17200',
            nameTR: 'Bakir-Berilyum C17200',
            category: 'non_ferrous_copper',
            density: 0.00830, meltingPoint: 955, thermalConductivity: 115, specificHeat: 418,
            tensileStrength: 1210, yieldStrength: 1000, elongation: 3, hardness: 380, elasticModulus: 130,
            machinabilityIndex: 35, weldabilityIndex: 50, castabilityIndex: 70, formabilityIndex: 25,
            pricePerKg: 45.0, priceCurrency: 'USD',
            shrinkageAllowance: 1.8, pouringTemperature: 1100, fluidityIndex: 75,
            compatibleProcesses: ['cnc_milling_3axis', 'cnc_milling_5axis', 'cnc_turning', 'cnc_edm', 'investment_casting'],
            minWallThickness: { cnc_milling_3axis: 0.8, cnc_milling_5axis: 0.8, investment_casting: 1.5 },
            minDraftAngle: { investment_casting: 0.5 },
            impactStrength: 30,
            fatigueStrength: 280,
            recyclable: true,
            roHSCompliant: false,
            materialType: 'metal'
        });

        this.addMaterial({
            id: 'KOVAR',
            name: 'Kovar (Fe-Ni-Co)',
            nameTR: 'Kovar (Fe-Ni-Co alasimi)',
            category: 'ferrous_steel',
            density: 0.00830, meltingPoint: 1450, thermalConductivity: 17, specificHeat: 439,
            tensileStrength: 517, yieldStrength: 345, elongation: 30, hardness: 150, elasticModulus: 138,
            machinabilityIndex: 40, weldabilityIndex: 80, castabilityIndex: 55, formabilityIndex: 40,
            pricePerKg: 20.0, priceCurrency: 'USD',
            shrinkageAllowance: 2.5,
            compatibleProcesses: ['cnc_milling_3axis', 'cnc_turning', 'cnc_edm', 'welding_tig', 'welding_laser'],
            minWallThickness: { cnc_milling_3axis: 0.8 },
            minDraftAngle: {},
            recyclable: true,
            roHSCompliant: true,
            materialType: 'metal'
        });

        this.addMaterial({
            id: 'PA12_SLS',
            name: 'PA12 Nylon for SLS 3D Print',
            nameTR: 'PA12 Naylon (SLS 3D Baski)',
            category: 'polymer_thermoplastic',
            density: 0.00100, meltingPoint: 184, thermalConductivity: 0.16, specificHeat: 1650,
            tensileStrength: 48, yieldStrength: 45, elongation: 18, hardness: 75, elasticModulus: 1.7,
            machinabilityIndex: 60, weldabilityIndex: 0, castabilityIndex: 0, formabilityIndex: 30,
            pricePerKg: 50.0, priceCurrency: 'USD', powderPricePerKg: 50.0,
            shrinkageAllowance: 0.4,
            compatibleProcesses: ['sls_3d_print', 'mjf_3d_print'],
            minWallThickness: { sls_3d_print: 0.7, mjf_3d_print: 0.5 },
            minDraftAngle: {},
            impactStrength: 30,
            uvStability: 'fair',
            moistureAbsorption: 0.5,
            flammabilityRating: 'HB',
            recyclable: true,
            roHSCompliant: true,
            materialType: 'polymer'
        });

        this.addMaterial({
            id: 'SS316L_DMLS',
            name: 'Stainless Steel 316L for DMLS',
            nameTR: 'Paslanmaz Celik 316L (DMLS)',
            category: 'ferrous_stainless',
            density: 0.00800, meltingPoint: 1400, thermalConductivity: 16.3, specificHeat: 500,
            tensileStrength: 640, yieldStrength: 530, elongation: 40, hardness: 225, elasticModulus: 200,
            machinabilityIndex: 40, weldabilityIndex: 80, castabilityIndex: 50, formabilityIndex: 60,
            pricePerKg: 60.0, priceCurrency: 'USD', powderPricePerKg: 80.0,
            shrinkageAllowance: 0.8,
            compatibleProcesses: ['dmls_3d_print', 'cnc_milling_3axis', 'cnc_turning', 'cnc_grinding', 'welding_tig', 'welding_laser'],
            minWallThickness: { dmls_3d_print: 0.8, cnc_milling_3axis: 0.8 },
            minDraftAngle: {},
            impactStrength: 150,
            fatigueStrength: 250,
            chemicalResistance: { acids: 'good', solvents: 'excellent', alkalis: 'good' },
            recyclable: true,
            roHSCompliant: true,
            materialType: 'metal'
        });
    }

    private addMaterial(mat: MaterialProperties): void {
        this.materials.set(mat.id, mat);
    }

    // ============================================================
    // MAKINE PARAMETRELERI
    // ============================================================

    private initMachineParameters(): void {
        // Dokum
        this.machineParams.set('sand_casting', { hourlyRate: 35, currency: 'USD', setupTimeMinutes: 120, toolChangeSeconds: 0, efficiencyFactor: 0.75 });
        this.machineParams.set('die_casting', { hourlyRate: 60, currency: 'USD', setupTimeMinutes: 180, toolChangeSeconds: 0, efficiencyFactor: 0.85 });
        this.machineParams.set('investment_casting', { hourlyRate: 50, currency: 'USD', setupTimeMinutes: 240, toolChangeSeconds: 0, efficiencyFactor: 0.70 });
        this.machineParams.set('shell_mold_casting', { hourlyRate: 40, currency: 'USD', setupTimeMinutes: 150, toolChangeSeconds: 0, efficiencyFactor: 0.78 });
        this.machineParams.set('centrifugal_casting', { hourlyRate: 45, currency: 'USD', setupTimeMinutes: 90, toolChangeSeconds: 0, efficiencyFactor: 0.80 });

        // CNC
        this.machineParams.set('cnc_milling_3axis', { hourlyRate: 65, currency: 'USD', setupTimeMinutes: 45, toolChangeSeconds: 30, efficiencyFactor: 0.82 });
        this.machineParams.set('cnc_milling_5axis', { hourlyRate: 110, currency: 'USD', setupTimeMinutes: 60, toolChangeSeconds: 45, efficiencyFactor: 0.78 });
        this.machineParams.set('cnc_turning', { hourlyRate: 55, currency: 'USD', setupTimeMinutes: 30, toolChangeSeconds: 20, efficiencyFactor: 0.85 });
        this.machineParams.set('cnc_edm', { hourlyRate: 100, currency: 'USD', setupTimeMinutes: 90, toolChangeSeconds: 60, efficiencyFactor: 0.65 });
        this.machineParams.set('cnc_grinding', { hourlyRate: 80, currency: 'USD', setupTimeMinutes: 45, toolChangeSeconds: 30, efficiencyFactor: 0.75 });

        // Sac Metal
        this.machineParams.set('sheet_metal_laser', { hourlyRate: 80, currency: 'USD', setupTimeMinutes: 15, toolChangeSeconds: 0, efficiencyFactor: 0.88 });
        this.machineParams.set('sheet_metal_plasma', { hourlyRate: 50, currency: 'USD', setupTimeMinutes: 10, toolChangeSeconds: 0, efficiencyFactor: 0.85 });
        this.machineParams.set('sheet_metal_waterjet', { hourlyRate: 90, currency: 'USD', setupTimeMinutes: 15, toolChangeSeconds: 0, efficiencyFactor: 0.80 });
        this.machineParams.set('sheet_metal_punching', { hourlyRate: 45, currency: 'USD', setupTimeMinutes: 20, toolChangeSeconds: 15, efficiencyFactor: 0.90 });
        this.machineParams.set('sheet_metal_bending', { hourlyRate: 40, currency: 'USD', setupTimeMinutes: 15, toolChangeSeconds: 10, efficiencyFactor: 0.85 });

        // Enjeksiyon
        this.machineParams.set('injection_molding', { hourlyRate: 50, currency: 'USD', setupTimeMinutes: 120, toolChangeSeconds: 0, efficiencyFactor: 0.90 });
        this.machineParams.set('blow_molding', { hourlyRate: 45, currency: 'USD', setupTimeMinutes: 90, toolChangeSeconds: 0, efficiencyFactor: 0.85 });

        // 3D Baski
        this.machineParams.set('fdm_3d_print', { hourlyRate: 15, currency: 'USD', setupTimeMinutes: 10, toolChangeSeconds: 0, efficiencyFactor: 0.92 });
        this.machineParams.set('sla_3d_print', { hourlyRate: 25, currency: 'USD', setupTimeMinutes: 15, toolChangeSeconds: 0, efficiencyFactor: 0.88 });
        this.machineParams.set('sls_3d_print', { hourlyRate: 80, currency: 'USD', setupTimeMinutes: 30, toolChangeSeconds: 0, efficiencyFactor: 0.85 });
        this.machineParams.set('dmls_3d_print', { hourlyRate: 150, currency: 'USD', setupTimeMinutes: 60, toolChangeSeconds: 0, efficiencyFactor: 0.75 });
        this.machineParams.set('mjf_3d_print', { hourlyRate: 70, currency: 'USD', setupTimeMinutes: 25, toolChangeSeconds: 0, efficiencyFactor: 0.90 });

        // Dovme
        this.machineParams.set('open_die_forging', { hourlyRate: 55, currency: 'USD', setupTimeMinutes: 60, toolChangeSeconds: 0, efficiencyFactor: 0.78 });
        this.machineParams.set('closed_die_forging', { hourlyRate: 75, currency: 'USD', setupTimeMinutes: 120, toolChangeSeconds: 0, efficiencyFactor: 0.82 });

        // Kaynak
        this.machineParams.set('welding_mig', { hourlyRate: 40, currency: 'USD', setupTimeMinutes: 15, toolChangeSeconds: 0, efficiencyFactor: 0.80 });
        this.machineParams.set('welding_tig', { hourlyRate: 55, currency: 'USD', setupTimeMinutes: 20, toolChangeSeconds: 0, efficiencyFactor: 0.72 });
        this.machineParams.set('welding_spot', { hourlyRate: 30, currency: 'USD', setupTimeMinutes: 10, toolChangeSeconds: 0, efficiencyFactor: 0.92 });
        this.machineParams.set('welding_laser', { hourlyRate: 120, currency: 'USD', setupTimeMinutes: 30, toolChangeSeconds: 0, efficiencyFactor: 0.85 });

        // Isil Islem & Yuzey
        this.machineParams.set('heat_treatment', { hourlyRate: 25, currency: 'USD', setupTimeMinutes: 30, toolChangeSeconds: 0, efficiencyFactor: 0.90 });
        this.machineParams.set('surface_anodizing', { hourlyRate: 20, currency: 'USD', setupTimeMinutes: 20, toolChangeSeconds: 0, efficiencyFactor: 0.85 });
        this.machineParams.set('surface_plating', { hourlyRate: 30, currency: 'USD', setupTimeMinutes: 25, toolChangeSeconds: 0, efficiencyFactor: 0.80 });
        this.machineParams.set('surface_painting', { hourlyRate: 15, currency: 'USD', setupTimeMinutes: 15, toolChangeSeconds: 0, efficiencyFactor: 0.90 });
        this.machineParams.set('surface_powder_coating', { hourlyRate: 18, currency: 'USD', setupTimeMinutes: 20, toolChangeSeconds: 0, efficiencyFactor: 0.88 });
    }

    // ============================================================
    // TOLERANS MALIYET CARPANLARI
    // ============================================================

    private initToleranceCosts(): void {
        this.toleranceCosts = [
            { toleranceRange: '+-0.5mm', toleranceMM: 0.5, costMultiplier: 1.0 },
            { toleranceRange: '+-0.25mm', toleranceMM: 0.25, costMultiplier: 1.2 },
            { toleranceRange: '+-0.1mm', toleranceMM: 0.1, costMultiplier: 1.4 },
            { toleranceRange: '+-0.05mm', toleranceMM: 0.05, costMultiplier: 1.8, requiredProcess: 'Hassas isleme' },
            { toleranceRange: '+-0.025mm', toleranceMM: 0.025, costMultiplier: 2.5, requiredProcess: 'Taslama' },
            { toleranceRange: '+-0.01mm', toleranceMM: 0.01, costMultiplier: 4.0, requiredProcess: 'Taslama/Honlama' },
            { toleranceRange: '+-0.005mm', toleranceMM: 0.005, costMultiplier: 7.0, requiredProcess: 'Lepleme' },
            { toleranceRange: '+-0.001mm', toleranceMM: 0.001, costMultiplier: 12.0, requiredProcess: 'Superfinish' },
        ];
    }

    // ============================================================
    // YUZEY PURUZSUZLUK MALIYET CARPANLARI
    // ============================================================

    private initSurfaceFinishCosts(): void {
        this.surfaceFinishCosts = [
            { raValue: 12.5, description: 'Rough machining', descriptionTR: 'Kaba isleme', costMultiplier: 1.0, requiredProcess: 'Kaba frezeleme/tornalama' },
            { raValue: 6.3, description: 'Standard rough', descriptionTR: 'Standart kaba', costMultiplier: 1.0, requiredProcess: 'Frezeleme' },
            { raValue: 3.2, description: 'Standard finish', descriptionTR: 'Standart finish', costMultiplier: 1.1, requiredProcess: 'CNC frezeleme' },
            { raValue: 1.6, description: 'Fine machining', descriptionTR: 'Ince isleme', costMultiplier: 1.5, requiredProcess: 'Hassas CNC' },
            { raValue: 0.8, description: 'Precision', descriptionTR: 'Hassas', costMultiplier: 2.5, requiredProcess: 'Hassas isleme/taslama' },
            { raValue: 0.4, description: 'Grinding', descriptionTR: 'Taslama', costMultiplier: 4.0, requiredProcess: 'Taslama' },
            { raValue: 0.2, description: 'Fine grinding', descriptionTR: 'Ince taslama', costMultiplier: 7.0, requiredProcess: 'Ince taslama/lepleme' },
            { raValue: 0.1, description: 'Lapping', descriptionTR: 'Lepleme', costMultiplier: 10.0, requiredProcess: 'Lepleme' },
            { raValue: 0.05, description: 'Superfinishing', descriptionTR: 'Superfinish', costMultiplier: 15.0, requiredProcess: 'Superfinish/Polisaj' },
        ];
    }
}

// ============================================================
// SINGLETON
// ============================================================

let _materialDB: FikoMaterialDB | null = null;

export function getMaterialDB(): FikoMaterialDB {
    if (!_materialDB) {
        _materialDB = new FikoMaterialDB();
    }
    return _materialDB;
}

export function disposeMaterialDB(): void {
    _materialDB = null;
}
